import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {AsyncPipe, NgIf} from "@angular/common";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {filter, Observable, Subscription, take} from "rxjs";
import {ReportService} from "../../../services/report.service";
import {PopupService} from "../../../services/popup.service";
import {Store} from "@ngrx/store";
import {ReportState} from "../../../state/report/report.reducer";
import {selectEditPromptFailure, selectEditPromptSuccess} from "../../../state/report/report.selectors";
import {editPrompt} from "../../../state/report/report.actions";
import {fetchReport} from "../../../state/search/search.actions";

@Component({
  selector: 'app-edit-prompt',
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    ReactiveFormsModule
  ],
  templateUrl: './edit-prompt.component.html',
  styleUrl: './edit-prompt.component.scss'
})
export class EditPromptComponent implements OnChanges {

  @Input() isVisible: boolean = false;
  @Input() assistantPrompt: string = '';  // Input to receive assistantPrompt
  @Input() promptValue: string = null;      // Input to receive promptValue

  @Output() modalClosed: EventEmitter<void> = new EventEmitter();

  form: FormGroup;

  reportSubscription: Subscription;
  report: any;

  loading$: Observable<boolean>;

  constructor(
    public formBuilder: FormBuilder,
    private reportService: ReportService,
    private popupService: PopupService,
    private store: Store<{ report: ReportState }>
  ) {

    this.reportSubscription = reportService.report$.subscribe(
      report => { this.report = report; }
    );

    this.form = formBuilder.group(
      {
        prompt: ['', [Validators.required]]
      },
      { updateOn: "blur" }
    );

    this.loading$ = this.store.select(state => state.report.loading);

  }

  // Detect changes in the input properties and populate the form with promptValue
  ngOnChanges(changes: SimpleChanges) {
    // When promptValue changes, set it in the form
    if (changes['promptValue'] && this.promptValue) {
      this.form.patchValue({
        prompt: this.promptValue
      });
    }

    // When the modal becomes visible, re-apply the promptValue
    if (changes['isVisible'] && this.isVisible && this.promptValue) {
      this.form.patchValue({
        prompt: this.promptValue
      });
    }
  }

  closeModal() {
    this.isVisible = false;
    this.modalClosed.emit(); // Notify the parent component that the modal has been closed

    // Reset form, but we won't reset the prompt value to avoid clearing it unintentionally
    this.form.get('prompt')?.reset();  // Only reset the form control, not the whole form
  }

  editPrompt() {
    if (this.form.valid) {

      const search_id = this.report.id;
      const assistant_prompt_id = this.assistantPrompt;
      const prompt_value = this.form.value.prompt;

      // Dispatch the edit prompt action, including assistantPrompt
      this.store.dispatch(editPrompt({ search_id, assistant_prompt_id, prompt_value }));

      const editPromptSuccess$ = this.store.select(selectEditPromptSuccess);
      const editPromptFailure$ = this.store.select(selectEditPromptFailure);

      let editPromptSuccessSubscription: Subscription = null;
      let editPromptFailureSubscription: Subscription = null;

      // Subscribe to the edit prompt success
      editPromptSuccessSubscription = editPromptSuccess$.pipe(
        filter(response => response !== null),
        take(1) // Take the first occurrence
      ).subscribe({
        next: (response) => {
          console.log('[edit-prompt.component.ts] editPromptSuccessSubscription data:', response);

          // Close the modal
          this.closeModal();

          // Notify success
          this.popupService.notify('Prompt has been successfully edited!', 'Edit success', 'success');

          if (editPromptSuccessSubscription) {
            editPromptSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }

          // Dispatch fetch report
          this.store.dispatch(fetchReport({ reportId: search_id }));
        },
        error: (error) => {
          // Handle subscription error
          console.error('Edit prompt success subscription error:', error);

          if (editPromptSuccessSubscription) {
            editPromptSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        }
      });

      // Subscribe to the edit prompt failure
      editPromptFailureSubscription = editPromptFailure$.pipe(
        filter(error => error !== null), // Filter for failure
        take(1) // Take the first occurrence
      ).subscribe({
        next: (error) => {
          // Handle failure
          const errorMessage = error.error.message || 'Failed to edit the prompt.';
          this.popupService.notify(errorMessage, 'Edit prompt error', 'error');

          if (editPromptFailureSubscription) {
            editPromptFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle subscription error
          console.error('Edit prompt failure subscription error:', error);

          if (editPromptFailureSubscription) {
            editPromptFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        }
      });

    }
  }

}
